import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';

import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  ClockIcon,
  PaperAirplaneIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  ArrowUturnLeftIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { getAPInvoicesDashboard } from '../utils/api';

const APInvoicesDashboard = () => {
  usePageTitle('AP Dashboard');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await getAPInvoicesDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner" />
        <span>Loading dashboard...</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">Failed to load dashboard</div>
        <p className="page-subtitle">Please check your connection and try again</p>
      </div>
    );
  }

  // Ensure all values have defaults to prevent render errors
  const safeStats = {
    drafted: Number(stats.drafted) || 0,
    rework: Number(stats.rework) || 0,
    pendingApproval: Number(stats.pendingApproval) || 0,
    toBeAccounted: Number(stats.toBeAccounted) || 0,
    toBePaid: Number(stats.toBePaid) || 0,
    overdue: Number(stats.overdue) || 0,
    totalToBePaidEur: Number(stats.totalToBePaidEur) || 0,
    fxDateNote: stats.fxDateNote || null,
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">AP Invoices Dashboard</h1>
          <p className="page-subtitle">Overview of accounts payable invoices</p>
          {safeStats.fxDateNote && (
            <p className="page-subtitle" style={{ color: 'var(--color-accent-warning)', marginTop: '4px' }}>
              {safeStats.fxDateNote}
            </p>
          )}
        </div>
      </div>

      <div className="stats-grid">
        {/* Drafted */}
        <div className="stat-card" style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ position: 'absolute', bottom: 8, left: 8 }}
            onClick={() => navigate('/invoices?approval_status=draft')}
            title="View drafted invoices"
          >
            <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
          </button>
          <div className="stat-icon blue">
            <DocumentTextIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Drafted</div>
            <div className="stat-value">{safeStats.drafted}</div>
            <div className="stat-subtext">Invoices in draft status</div>
          </div>
        </div>

        {/* Set to Rework */}
        <div className="stat-card" style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ position: 'absolute', bottom: 8, left: 8 }}
            onClick={() => navigate('/invoices?approval_status=rework')}
            title="View rework invoices"
          >
            <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
          </button>
          <div className="stat-icon orange">
            <ArrowUturnLeftIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Set to Rework</div>
            <div className="stat-value">{safeStats.rework}</div>
            <div className="stat-subtext">Invoices needing revision</div>
          </div>
        </div>

        {/* Pending Approval */}
        <div className="stat-card" style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ position: 'absolute', bottom: 8, left: 8 }}
            onClick={() => navigate('/invoices?approval_status=pending_approval')}
            title="View pending approval invoices"
          >
            <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
          </button>
          <div className="stat-icon yellow">
            <ClockIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Pending Approval</div>
            <div className="stat-value">{safeStats.pendingApproval}</div>
            <div className="stat-subtext">Waiting for approval</div>
          </div>
        </div>

        {/* To Be Sent to Accounting */}
        <div className="stat-card" style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ position: 'absolute', bottom: 8, left: 8 }}
            onClick={() => navigate('/invoices?approval_status=approved&accounting_status=not_sent')}
            title="View invoices to be accounted"
          >
            <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
          </button>
          <div className="stat-icon purple">
            <PaperAirplaneIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">To Be Accounted</div>
            <div className="stat-value">{safeStats.toBeAccounted}</div>
            <div className="stat-subtext">Approved, not sent yet</div>
          </div>
        </div>

        {/* To Be Paid */}
        <div className="stat-card" style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ position: 'absolute', bottom: 8, left: 8 }}
            onClick={() => navigate('/invoices?payment_status=not_paid')}
            title="View invoices to be paid"
          >
            <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
          </button>
          <div className="stat-icon green">
            <CreditCardIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">To Be Paid</div>
            <div className="stat-value">{safeStats.toBePaid}</div>
            <div className="stat-subtext">
              Total: €{safeStats.totalToBePaidEur.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}
            </div>
          </div>
        </div>

        {/* Overdue */}
        <div className="stat-card" style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            style={{ position: 'absolute', bottom: 8, left: 8 }}
            onClick={() => navigate('/invoices?overdue_status=overdue&payment_status=not_paid')}
            title="View overdue invoices"
          >
            <ArrowTopRightOnSquareIcon style={{ width: 16, height: 16 }} />
          </button>
          <div className="stat-icon red">
            <ExclamationTriangleIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Overdue</div>
            <div className="stat-value">{safeStats.overdue}</div>
            <div className="stat-subtext">Past due date, unpaid</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APInvoicesDashboard;
